// icons.js — one named export per MEANING, never per shape (NIGHT_SHIFT.md
// item 6 / STATUS.md, 2026-07-19). Alex: "if we decide to change one, all the
// same must change." Before this file, glyphs were typed inline across
// App.jsx and TreeNav.jsx (✎ ✕ ⧉ ⠿ ↗ ⚠ ✓ ✗ ∅ ◐ ○ ⟲ 🔒 —) — changing what
// "edit" looks like meant finding every literal by hand. Now it is one
// constant.
//
// Name by MEANING (WARN, not TRIANGLE) — the point is the glyph can change
// without the name changing. scripts/check-style.mjs enforces no bare glyph
// literal outside this file (in the covered files) so the wrong way fails
// the build, same allowlist/ratchet pattern as the hex-color guard.
//
// Not every icon is a glyph string (NIGHT_SHIFT.md item 7b, 2026-07-21) — the
// Onshape cube (OnshapeMark, below) is an SVG. It lives here too, built with
// createElement rather than JSX: this file stays a plain `.js` (esbuild's
// loader only JSX-transforms `.jsx`, per build-web.mjs), so no import site
// across the codebase has to change its extension. Same "one home for
// icons" rule, whatever form the icon takes.
import { createElement } from 'react';

export const EDIT = '✎';
export const DELETE = '✕';
export const COPY = '⧉';
export const DRAG = '⠿';
export const LINK = '↗';
export const WARN = '⚠';
export const OK = '✓';
export const VALIDATED_SYNCED = '✓✓';
export const DEPRECATED = '✗';
export const EMPTY = '∅';
export const DRAFT = '○';
export const IN_PROGRESS = '◐';
export const RESTORE = '⟲';
export const LOCKED = '🔒';
export const DASH = '—';

// NIGHT_SHIFT.md item 7 (2026-07-20) — Alex: "Some are missing — like the one
// that informs Onshape Sync, and the ones for catalog, product and Onshape
// 3D." CATALOG/PRODUCT close a real fork: the same two glyphs were
// hand-written identically in OnshapePanel.jsx, product-cols.jsx and
// ProductsView.jsx (the ▣/⊞ Source-kind pick) — changing one meant finding
// three literals. SYNC/VIEW_3D are reserved here for the two text-only
// buttons that have no icon at all today ("Preview component sync",
// "View 3D") — not yet wired into either button, so there is nothing to
// verify pixel-identical; a future pass can import them without inventing a
// new glyph. (No ONSHAPE export: the brand marker beside a db_id —
// OnshapeLink.jsx — is an inline SVG, not a glyph, so there is nothing to
// name here.)
export const CATALOG = '▣';
export const PRODUCT = '⊞';
export const SYNC = '⇄';
export const VIEW_3D = '⛶';

// Section collapse control (UI_VISION §4 — "the title is the collapse
// control"). Named by STATE, not by shape: SECTION_OPEN is what a section
// shows WHEN IT IS OPEN (so clicking it closes). Item 7C listed `▾`/`▸` as
// unnamed glyphs; these are those two, named.
// DISCLOSURE — "this opens and closes", the ONE open/close marker (Alex,
// 2026-08-13: "Is it the chevron that open close something. If yes, it must be the
// same."). Rendered only through components/Chevron.jsx, which owns the rotation
// and the size too — a name alone standardises the shape and nothing else, which is
// how the tree and the section titles drifted while importing the same constants.
//
// It replaces SECTION_OPEN '▾' / SECTION_CLOSED '▸'. Two glyphs are not needed once
// the marker rotates, and `▸` was needed elsewhere: it is now the path separator,
// because the two meanings had been sharing both shapes in a crossed pair.
export const DISCLOSURE = '›';

// CRUMB_SEP — "this path continues", between the parts of a breadcrumb or a
// location string. NEVER a control: if it can be clicked to open something it is a
// DISCLOSURE, and the whole point of splitting these two is that one glyph stopped
// meaning both.
export const CRUMB_SEP = '▸';

// DROPDOWN — "click to see a list of choices". Distinct from DISCLOSURE on
// purpose: it never shows a state, it is the same glyph open or shut, and it marks
// a PICKER rather than a region that expands in place. Rendered only by
// CascadeSelect / SelectCell, the two pick primitives. ⚠ It briefly lost its name
// on 2026-08-13 when SECTION_OPEN was retired — same shape, and the derived style
// guard went quiet on it until it was named again here.
export const DROPDOWN = '▾';

// FORMULA — "there is an expression behind this value" (Alex, 2026-07-23:
// "Maybe the F sign if there is a formula inside"). The read-mode marker on a
// formula-backed numeric cell AND FormulaCell's edit-mode trigger, so the two
// can never drift apart.
export const FORMULA = 'ƒ';

// NIGHT_SHIFT.md item 7b (2026-07-21) — the "value came from Onshape" marker on
// CONFIGURATION params (_mass_kg/_surface_m2) is `OnshapeMark` below, NOT a glyph.
// Two predecessors were rejected; both reasons are worth keeping:
//   `⟳` collided with RESTORE (`⟲`, undo/history) — they differ ONLY by which
//        way the arrow curls, breaking "shape = meaning, no colour signal"
//        (Alex is colour-blind) as badly as colour-coding would.
//   `⚙`  removed that collision but read as SETTINGS — the near-universal UI
//        meaning — and the marker sits on rows inside the CONFIGURATION section,
//        where every row already IS a setting. It said the wrong thing clearly.
// The Onshape cube says the one thing meant: this came from Onshape. Reaching
// for the brand mark was only possible once icons.js could hold a non-glyph
// icon — which is the other half of item 7b.

// OnshapeMark({ size = 14, ...props }) — the "open this in Onshape" cube,
// moved here from OnshapeLink.jsx (item 7b part B: icons.js exported glyph
// STRINGS only, so this SVG was structurally forced to live outside the icon
// system — the actual gap item 7 flagged and skipped). Markup is byte-for-byte
// what OnshapeLink rendered inline; `size` replaces the old hardcoded 14/14.
export function OnshapeMark({ size = 14, ...props }) {
  return createElement(
    'svg',
    {
      width: size,
      height: size,
      viewBox: '0 0 24 24',
      fill: 'none',
      stroke: 'currentColor',
      strokeWidth: '1.8',
      strokeLinejoin: 'round',
      strokeLinecap: 'round',
      'aria-hidden': 'true',
      focusable: 'false',
      ...props,
    },
    createElement('path', { key: 'a', d: 'M12 2 21 7 21 17 12 22 3 17 3 7 Z' }),
    createElement('path', { key: 'b', d: 'M12 2 12 12 21 7' }),
    createElement('path', { key: 'c', d: 'M12 12 12 22' }),
    createElement('path', { key: 'd', d: 'M12 12 3 7' }),
  );
}

// Model3DMark — the 3D MODEL view's icon. THE SAME DRAWING as OnshapeMark, on
// purpose, and named separately because this file's rule is one export per
// MEANING (Alex, 2026-08-27, choosing it over a glyph: "I prefered the previous
// one and then the Onshape you are proposing").
//
// ⚠ IT IS A CUBE, NOT A LOGO. The geometry is a plain isometric wireframe cube —
// a hexagonal outline with three edges meeting at the centre — so it says "3D
// model", not "Onshape". That is what makes it usable on a menu item whose label
// was deliberately de-branded (2026-07-23: "Replace ONSHAPE w/ 3D MODEL in the
// menu also"); reaching for the vendor's actual logo there would undo that.
//
// Why an alias and not a copy: the two meanings share one drawing today, and a
// copy would be a fork that drifts silently. Two names means either can be given
// its own geometry later WITHOUT hunting down which call sites meant which — the
// whole reason this file names by meaning in the first place.
//
// ⚠ Why the OTHER two menu icons stay GLYPHS (▣ Catalog, ⊞ Products). Measured
// 2026-08-27 at 13px: the three current glyphs carry 68 / 75 / **29** ink pixels
// — `⛶` (U+26F6 SQUARE FOUR CORNERS, the media-player FULLSCREEN control) was
// both the wrong meaning and half the visual weight of its neighbours. Catalog
// and Products were never wrong in the same way, so only the broken one moved.
// The row is therefore mixed glyph + SVG, which is a deliberate exception and
// not a drift: the rule underneath is that Unicode has good marks for actions
// and states and almost none for THINGS, and "a 3D model" is a thing.
export const Model3DMark = OnshapeMark;
